'use client';

import * as React from 'react';
import { type LucideIcon } from 'lucide-react';

import { cn } from '../lib/utils';

export interface ButtonToggleSelectorOption<T extends string> {
  value: T;
  label: string;
  /** Optional leading icon, rendered before the label and hidden from a11y. */
  icon?: LucideIcon;
}

export interface ButtonToggleSelectorProps<T extends string> {
  /**
   * Current selected value(s)
   * - For single mode: T | undefined
   * - For multiple mode: T[]
   */
  value?: T | T[];

  /**
   * Callback when selection changes
   * - For single mode: (value: T | undefined) => void
   * - For multiple mode: (value: T[]) => void
   */
  onChange: (value: T | T[] | undefined) => void;

  /**
   * Available options to select from
   */
  options: ButtonToggleSelectorOption<T>[];

  /**
   * Selection mode
   * - 'single': Only one option can be selected at a time
   * - 'multiple': Multiple options can be selected (default)
   */
  mode?: 'single' | 'multiple';

  /**
   * Custom className for the container
   */
  className?: string;

  /**
   * Custom className for individual buttons
   */
  buttonClassName?: string;

  /**
   * Disabled state
   */
  disabled?: boolean;

  /**
   * ARIA label for accessibility
   */
  'aria-label'?: string;
}

/**
 * ButtonToggleSelector - Reusable button-based toggle selector component
 *
 * Features:
 * - Single or multiple selection modes
 * - Mobile-optimized with 44x44px minimum touch targets
 * - Type-safe with TypeScript generics
 * - Accessible with ARIA labels and keyboard navigation
 * - Customizable styling
 *
 * @example
 * // Single select
 * <ButtonToggleSelector
 *   mode="single"
 *   value={selectedStyle}
 *   onChange={setSelectedStyle}
 *   options={[
 *     { value: 'classical', label: 'Classical Ballet' },
 *     { value: 'contemporary', label: 'Contemporary' }
 *   ]}
 * />
 *
 * @example
 * // Multiple select (default)
 * <ButtonToggleSelector
 *   value={selectedStyles}
 *   onChange={setSelectedStyles}
 *   options={danceStyleOptions}
 * />
 */
export function ButtonToggleSelector<T extends string>({
  value,
  onChange,
  options,
  mode = 'multiple',
  className,
  buttonClassName,
  disabled = false,
  'aria-label': ariaLabel,
}: ButtonToggleSelectorProps<T>) {
  const handleToggle = React.useCallback(
    (optionValue: T) => {
      if (disabled) return;

      if (mode === 'single') {
        // Single select mode: toggle on/off or switch to new value
        const currentValue = value as T | undefined;
        const newValue = currentValue === optionValue ? undefined : optionValue;
        onChange(newValue);
      } else {
        // Multiple select mode: add/remove from array
        const currentValues = (value as T[] | undefined) || [];
        const isSelected = currentValues.includes(optionValue);

        const newValues = isSelected
          ? currentValues.filter((v) => v !== optionValue)
          : [...currentValues, optionValue];

        onChange(newValues);
      }
    },
    [value, onChange, mode, disabled],
  );

  const isSelected = React.useCallback(
    (optionValue: T): boolean => {
      if (mode === 'single') {
        return value === optionValue;
      } else {
        const currentValues = (value as T[] | undefined) || [];
        return currentValues.includes(optionValue);
      }
    },
    [value, mode],
  );

  return (
    <div
      role={mode === 'single' ? 'radiogroup' : 'group'}
      aria-label={ariaLabel}
      className={cn('flex flex-wrap gap-2', className)}
    >
      {options.map((option) => {
        const selected = isSelected(option.value);

        return (
          <button
            key={option.value}
            type="button"
            role={mode === 'single' ? 'radio' : 'checkbox'}
            aria-checked={selected}
            aria-label={option.label}
            disabled={disabled}
            onClick={() => handleToggle(option.value)}
            className={cn(
              // Base styles
              'inline-flex items-center justify-center gap-2',
              'rounded-md px-4 py-3 text-sm font-medium',
              'transition-all duration-200',
              'cursor-pointer select-none',

              // Mobile optimization - WCAG AAA minimum touch target
              'min-h-[44px] min-w-[44px]',

              // Touch feedback
              'active:scale-95',

              // States
              selected
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-secondary text-secondary-foreground hover:bg-secondary/80',

              // Disabled state
              disabled && 'cursor-not-allowed opacity-50',

              // Focus state (keyboard navigation)
              'focus-visible:ring-ring focus:outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2',

              // Custom className
              buttonClassName,
            )}
          >
            {option.icon ? (
              <option.icon className="h-4 w-4 shrink-0" aria-hidden="true" />
            ) : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
